import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, useMap, useMapEvents } from "react-leaflet";
import { LocateFixed, MapPin } from "lucide-react";

const DEFAULT_DELIVERY_CENTER = [24.7136, 46.6753];

function RecenterMap({ position }) {
  const map = useMap();

  useEffect(() => {
    map.setView(position, 16);
  }, [map, position]);

  return null;
}

function MapCenterLocationSelector({ onSelect, onDraggingChange }) {
  const shouldUpdateCenterRef = useRef(false);
  const map = useMapEvents({
    dragstart: () => {
      shouldUpdateCenterRef.current = true;
      onDraggingChange(true);
    },
    moveend: () => {
      if (!shouldUpdateCenterRef.current) {
        return;
      }
      shouldUpdateCenterRef.current = false;
      onDraggingChange(false);
      const center = map.getCenter();
      onSelect(center.lat, center.lng);
    },
  });

  return null;
}

export const DeliveryLocationPicker = ({
  latitude,
  longitude,
  onLocationChange,
  onAddressDetected,
  title = "موقع التوصيل",
  instruction = "حرّك الخريطة حتى يصبح الدبوس فوق موقع التوصيل، أو استخدم موقعك الحالي.",
  showLocationHint = false,
}) => {
  const [notice, setNotice] = useState("");
  const [detectedAddress, setDetectedAddress] = useState("");
  const [isLookingUpAddress, setIsLookingUpAddress] = useState(false);
  const [isMapDragging, setIsMapDragging] = useState(false);
  const [isLocationTooltipVisible, setIsLocationTooltipVisible] = useState(false);
  const requestIdRef = useRef(0);
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const lastSuccessfulKeyRef = useRef(null);
  const lastSuccessfulAddressRef = useRef("");
  const pendingKeyRef = useRef(null);
  const hasLocation = Number.isFinite(latitude) && Number.isFinite(longitude);
  const hasLocationRef = useRef(hasLocation);
  const position = useMemo(
    () => (hasLocation ? [latitude, longitude] : DEFAULT_DELIVERY_CENTER),
    [hasLocation, latitude, longitude],
  );

  useEffect(() => () => {
    abortControllerRef.current?.abort();
    window.clearTimeout(debounceTimerRef.current);
  }, []);

  useEffect(() => {
    hasLocationRef.current = hasLocation;
    if (hasLocation) {
      setIsLocationTooltipVisible(false);
    }
  }, [hasLocation]);

  useEffect(() => {
    if (!showLocationHint) {
      setIsLocationTooltipVisible(false);
      return undefined;
    }

    const tooltipTimer = window.setTimeout(() => {
      if (!hasLocationRef.current) {
        setIsLocationTooltipVisible(true);
      }
    }, 1000);

    return () => window.clearTimeout(tooltipTimer);
  }, [showLocationHint]);

  const updateLocation = useCallback((nextLatitude, nextLongitude) => {
    const normalizedLatitude = Number(nextLatitude.toFixed(6));
    const normalizedLongitude = Number(nextLongitude.toFixed(6));
    const coordinateKey = `${normalizedLatitude},${normalizedLongitude}`;

    onLocationChange({
      latitude: normalizedLatitude,
      longitude: normalizedLongitude,
    });
    if (coordinateKey === lastSuccessfulKeyRef.current) {
      setDetectedAddress(lastSuccessfulAddressRef.current);
      onAddressDetected?.(lastSuccessfulAddressRef.current);
      setNotice("");
      return;
    }

    window.clearTimeout(debounceTimerRef.current);
    abortControllerRef.current?.abort();
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    pendingKeyRef.current = coordinateKey;
    setDetectedAddress("");
    onAddressDetected?.("");
    setIsLookingUpAddress(true);

    debounceTimerRef.current = window.setTimeout(async () => {
      if (pendingKeyRef.current !== coordinateKey) {
        return;
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const query = new URLSearchParams({
          lat: String(normalizedLatitude),
          lon: String(normalizedLongitude),
        });
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/delivery/reverse-geocode?${query}`,
          { signal: controller.signal },
        );
        if (!response.ok) {
          throw new Error("Reverse geocoding is unavailable.");
        }
        const payload = await response.json();
        if (requestId !== requestIdRef.current) {
          return;
        }
        if (payload.display_name) {
          lastSuccessfulKeyRef.current = coordinateKey;
          lastSuccessfulAddressRef.current = payload.display_name;
          setDetectedAddress(payload.display_name);
          onAddressDetected?.(payload.display_name);
          setNotice("");
        } else {
          setNotice("تعذر تحديد العنوان تلقائيًا. راجع العلامة ثم أكمل الطلب.");
        }
      } catch (error) {
        if (error.name !== "AbortError" && requestId === requestIdRef.current) {
          setNotice("تعذر تحديد العنوان تلقائيًا. راجع العلامة ثم أكمل الطلب.");
        }
      } finally {
        if (requestId === requestIdRef.current) {
          pendingKeyRef.current = null;
          setIsLookingUpAddress(false);
        }
      }
    }, 800);
  }, [onAddressDetected, onLocationChange]);

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      setNotice("الموقع الجغرافي غير مدعوم في هذا المتصفح.");
      return;
    }

    setNotice("جارٍ تحديد موقعك…");
    navigator.geolocation.getCurrentPosition(
      (positionResult) => {
        updateLocation(positionResult.coords.latitude, positionResult.coords.longitude);
        setNotice("تم تحديد موقع التوصيل. حرّك الخريطة لتعديل الموقع.");
      },
      (error) => {
        const message = error.code === error.PERMISSION_DENIED
          ? "تم رفض إذن الموقع. حدد موقع التوصيل يدويًا من الخريطة."
          : "تعذر تحديد موقعك. حدد موقع التوصيل يدويًا من الخريطة.";
        setNotice(message);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      },
    );
  };

  return (
    <div className="space-y-2" data-testid="delivery-location-section">
      <div
        className="text-sm font-bold text-gray-700"
        data-testid="delivery-location-title"
      >
        {title}
      </div>
      <div className="text-xs text-gray-600" data-testid="delivery-location-instruction">
        {instruction}
      </div>
      <div className="delivery-location-map relative" data-testid="delivery-location-map">
        <MapContainer center={position} zoom={hasLocation ? 16 : 11} className="h-full w-full">
          <TileLayer
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={19}
            attribution="&copy; OpenStreetMap contributors"
          />
          <MapCenterLocationSelector
            onSelect={updateLocation}
            onDraggingChange={setIsMapDragging}
          />
          {hasLocation && <RecenterMap position={position} />}
        </MapContainer>
        <div
          className={
            "pointer-events-none absolute left-1/2 top-1/2 z-[1000] -translate-x-1/2 "
            + "-translate-y-full text-orange-600 drop-shadow-md"
          }
          data-testid="delivery-center-pin"
          aria-hidden="true"
        >
          <div
            className={
              "block transition-transform duration-150 ease-out "
              + (isMapDragging ? "-translate-y-1.5" : "translate-y-0")
            }
            data-testid="delivery-center-pin-icon"
          >
            <MapPin size={38} fill="currentColor" strokeWidth={1.7} />
          </div>
        </div>
        <button
          type="button"
          onClick={useMyLocation}
          className={
            "absolute bottom-3 right-3 z-[1000] grid h-10 w-10 place-items-center rounded-full "
            + "border border-orange-200 bg-white text-orange-700 shadow-md"
          }
          aria-label="استخدم موقعي"
          title="استخدم موقعي"
          data-testid="use-my-location-button"
        >
          <LocateFixed size={18} />
        </button>
        {showLocationHint && !hasLocation && isLocationTooltipVisible && (
          <div
            className={
              "pointer-events-none absolute bottom-[3.75rem] right-3 z-[1000] rise-in "
              + "rounded-lg border border-black bg-black px-2 py-1 text-[11px] "
              + "font-medium text-white shadow-sm"
            }
            data-testid="delivery-use-my-location-tooltip"
          >
            <span>حدد موقعك</span>
            <span
              className={
                "absolute -bottom-1 right-4 h-0 w-0 border-x-4 border-t-4 "
                + "border-x-transparent border-t-black"
              }
              data-testid="delivery-use-my-location-tooltip-pointer"
              aria-hidden="true"
            />
          </div>
        )}
      </div>
      {hasLocation && (
        <div
          className="flex items-center gap-1 text-xs text-gray-600"
          data-testid="delivery-location-coordinates"
        >
          <MapPin size={14} />
          {latitude.toFixed(6)}, {longitude.toFixed(6)}
        </div>
      )}
      {isLookingUpAddress && (
        <div className="text-xs text-gray-600" data-testid="reverse-geocode-loading">
          جارٍ تحديد العنوان…
        </div>
      )}
      {detectedAddress && (
        <div className="text-xs text-gray-700" data-testid="detected-delivery-address">
          العنوان المكتشف: {detectedAddress}
        </div>
      )}
      {notice && notice !== "تعذر تحديد العنوان تلقائيًا. راجع العلامة ثم أكمل الطلب." && (
        <div className="text-xs text-gray-600" data-testid="delivery-location-notice">
          {notice}
        </div>
      )}
    </div>
  );
};